import { clsx } from 'clsx';
import { type PointerEvent, type ReactNode, useId, useRef, useState } from 'react';
import { uiCopy } from './copy';
import { TopDialog } from './Dialog';

/**
 * Bottom sheet in the browser's top layer (portal plus showModal). The handle is a real button:
 * tap it or drag the header down to close. Escape and a backdrop tap close it too, unless
 * `dismissible` is false. Content scrolls inside; the footer sticks above the home indicator.
 *
 *   <Sheet open={open} onClose={() => setOpen(false)} title="Pick the shooter"
 *     footer={<Button block variant="primary">Done</Button>}>...</Sheet>
 */
export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Visually hide the title (it still names the dialog). */
  hideTitle?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  /** 'auto' hugs the content up to 92 percent of the screen, 'tall' always takes 92 percent. */
  height?: 'auto' | 'tall';
  dismissible?: boolean;
  closeLabel?: string;
}

const CLOSE_AT = 90;

export function Sheet(props: SheetProps) {
  if (!props.open) return null;
  return <SheetBody {...props} />;
}

function SheetBody({
  onClose,
  title,
  hideTitle,
  children,
  footer,
  height = 'auto',
  dismissible = true,
  closeLabel = uiCopy.close,
}: SheetProps) {
  const id = useId();
  const [dy, setDy] = useState(0);
  const start = useRef<number | null>(null);

  const down = (e: PointerEvent<HTMLElement>) => {
    if (!dismissible) return;
    start.current = e.clientY;
  };
  const move = (e: PointerEvent<HTMLElement>) => {
    if (start.current === null) return;
    const d = Math.max(0, e.clientY - start.current);
    // Capture only once it is a drag, so a plain tap still reaches the handle button.
    if (d > 6 && !e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.setPointerCapture?.(e.pointerId);
    setDy(d > 6 ? d : 0);
  };
  const up = () => {
    if (start.current === null) return;
    start.current = null;
    if (dy > CLOSE_AT) onClose();
    else setDy(0);
  };

  return (
    <TopDialog onClose={onClose} dismissible={dismissible} labelledBy={`${id}-t`}>
      <div className="pointer-events-none flex h-full items-end justify-center">
        <div
          className={clsx(
            'tts-ui-sheet-panel pointer-events-auto flex w-full max-w-lg flex-col rounded-t-[10px] border-x-(length:--tts-border-heavy) border-t-(length:--tts-border-heavy) border-ink bg-white text-ink shadow-[0_-6px_0_0_var(--color-shade)]',
            height === 'tall' ? 'h-[92dvh]' : 'max-h-[92dvh]',
          )}
          style={dy ? { transform: `translateY(${dy}px)`, transition: 'none' } : undefined}
        >
          <div
            className="flex shrink-0 cursor-grab touch-none flex-col items-center"
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
          >
            <button
              type="button"
              aria-label={closeLabel}
              disabled={!dismissible}
              onClick={() => dy === 0 && onClose()}
              className="flex h-11 w-24 items-center justify-center disabled:cursor-default"
            >
              <span aria-hidden="true" className="block h-1.5 w-12 rounded-full bg-ink" />
            </button>
            <h2 id={`${id}-t`} className={hideTitle ? 'sr-only' : 'tts-display w-full px-5 pb-3 text-2xl'}>
              {title}
            </h2>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4">{children}</div>
          {footer && (
            <div className="tts-pb-safe shrink-0 border-t-(length:--tts-border) border-ink bg-lilac-pale px-5 pt-3">
              <div className="pb-3">{footer}</div>
            </div>
          )}
          {!footer && <div className="tts-pb-safe shrink-0" />}
        </div>
      </div>
    </TopDialog>
  );
}
